import { PulseIndicator } from "@/components/common/pulse-indicator";
import {
  sortDirectionEnum,
  type SortDirectionType,
  type SortOptionsTypes,
} from "@/types/common/filters";
import { cn } from "@/utils/theme";
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Radiobutton,
} from "@elaniintc/goes-design-system";
import { useState } from "react";

const sortDirections: SortOptionsTypes[] = [
  { label: "Ascendente", value: sortDirectionEnum.asc },
  { label: "Descendente", value: sortDirectionEnum.desc },
];

interface SortDropdownProps {
  sortOptions: SortOptionsTypes[];
  onClick: (field: string | undefined, direction: SortDirectionType) => void;
  currentOrderField: string | undefined;
  currentOrderDirection: SortDirectionType;
  buttonSize?: "sm" | "default" | "lg";
}

export default function SortDropdown({
  sortOptions,
  onClick,
  currentOrderField,
  currentOrderDirection,
  buttonSize = "default",
}: SortDropdownProps) {
  const [open, setOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<string | undefined>(
    currentOrderField,
  );
  const [selectedDirection, setSelectedDirection] = useState<SortDirectionType>(
    currentOrderDirection,
  );

  const applySort = () => {
    setOpen(false);
    onClick(selectedField, selectedDirection);
  };

  const isDisabled =
    currentOrderField === selectedField &&
    currentOrderDirection === selectedDirection;
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="quiet"
          className="relative border border-neutral leading-2xl bg-neutral-object shadow-custom py-1.5 px-2 rounded-lg min-w-9 gap-2 font-semibold text-body text-sm font-geist cursor-pointer"
          size={buttonSize}
        >
          Ordenar por
          <Icon type="far" name="angles-up-down" className="text-current" />
          <PulseIndicator showIndicator />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[280px] rounded-lg border-neutral-silent text-body font-normal px-2 py-4 shadow-custom-lg">
        {sortOptions.map((field) => (
          <DropdownMenuItem
            key={field.value}
            onSelect={(e) => e.preventDefault()}
            className={cn(
              "flex justify-between px-3 py-2 gap-0 h-11 w-min-[264px] cursor-pointer text-base font-normal font-geist text-body ",
              selectedField === field.value
                ? "bg-surface-action-accent"
                : "hover:bg-neutral-object-accent-silent",
            )}
            onClick={() => setSelectedField(field.value)}
          >
            {field.label}
            {selectedField === field.value && (
              <Checkbox
                className="w-5 h-5 focus:ring-1! font-bold"
                id={field.value}
                checked={selectedField === field.value}
                onChange={() => {}}
              />
            )}
          </DropdownMenuItem>
        ))}

        {sortOptions.length > 0 && (
          <DropdownMenuSeparator className="h-[1px] bg-[#B7C0CC] m-3" />
        )}

        {sortDirections.map((dir) => (
          <DropdownMenuItem
            key={dir.value}
            onSelect={(e) => e.preventDefault()}
            className={
              "flex justify-between px-3 py-2 gap-0 h-11 w-min-[264px] cursor-pointer text-base font-normal font-geist text-body"
            }
          >
            {dir.label}
            <Radiobutton
              checked={selectedDirection === dir.value}
              className="cursor-pointer"
              onClick={() =>
                setSelectedDirection(dir.value as SortDirectionType)
              }
              value={dir.value}
            />
          </DropdownMenuItem>
        ))}

        <div className="p-2 pt-3">
          <Button
            variant="quiet"
            onClick={applySort}
            className={cn(
              "w-full cursor-pointer rounded-lg border font-semibold font-geist text-base",
              isDisabled
                ? "border-neutral-silent/20 text-on-disabled"
                : "border-neutral shadow-card text-body",
            )}
            disabled={isDisabled}
          >
            Aplicar
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
